import type { ITuningClock } from '@playcastdotio/PeerCore/tuning';

type SchedulingSink = { observeTiming(name: string, milliseconds: number, observedAtMs?: number): void;
  setContext(visibility: string): void; observeAvailability?(name: string, availability: 'not-sampled' | 'unsupported'): void };
type BrowserSurface = { document?: Document; requestAnimationFrame?: (callback: FrameRequestCallback) => number;
  cancelAnimationFrame?: (id: number) => void; PerformanceObserver?: typeof PerformanceObserver };

/** Opt-in scheduling evidence, separate from the media observer. No frames or input are captured. */
export class BrowserSchedulingMonitor {
  private timer: ReturnType<typeof setInterval> | undefined;
  private raf: number | undefined;
  private observer: PerformanceObserver | undefined;
  private detachVisibility: (() => void) | undefined;
  private generation = 0;
  constructor(private readonly clock: ITuningClock, private readonly sink: SchedulingSink,
    private readonly surface: BrowserSurface = globalThis) {}
  start(): void {
    this.stop(); const generation = this.generation;
    let scheduled = this.clock.monotonicMs() + 250;
    this.timer = setInterval(() => {
      const now = this.clock.monotonicMs(); this.sink.observeTiming('mainThreadTimerDelay', Math.max(0, now - scheduled), now);
      scheduled = now + 250;
    }, 250);
    const visibility = () => this.sink.setContext(this.surface.document?.visibilityState ?? 'unknown');
    visibility(); this.surface.document?.addEventListener('visibilitychange', visibility);
    this.detachVisibility = () => this.surface.document?.removeEventListener('visibilitychange', visibility);
    let last: number | undefined;
    const frame = (now: number) => {
      if (generation !== this.generation) return;
      if (last !== undefined) this.sink.observeTiming('mainThreadAnimationGap', Math.max(0, now - last), now);
      last = now; this.raf = this.surface.requestAnimationFrame?.(frame);
    };
    this.raf = this.surface.requestAnimationFrame?.(frame);
    const Constructor = this.surface.PerformanceObserver;
    this.sink.observeAvailability?.('mainThreadLongTask', Constructor?.supportedEntryTypes?.includes('longtask') ? 'not-sampled' : 'unsupported');
    this.sink.observeAvailability?.('mainThreadAnimationGap', this.surface.requestAnimationFrame ? 'not-sampled' : 'unsupported');
    if (Constructor?.supportedEntryTypes?.includes('longtask')) {
      this.observer = new Constructor((entries) => {
        if (generation !== this.generation) return;
        for (const entry of entries.getEntries().slice(0, 32)) this.sink.observeTiming('mainThreadLongTask', entry.duration, this.clock.monotonicMs());
      });
      try { this.observer.observe({ entryTypes: ['longtask'] }); } catch { this.observer.disconnect(); this.observer = undefined; }
    }
  }
  capabilities(): { longTasks: boolean; animationFrames: boolean } {
    return { longTasks: this.surface.PerformanceObserver?.supportedEntryTypes?.includes('longtask') ?? false,
      animationFrames: typeof this.surface.requestAnimationFrame === 'function' };
  }
  stop(): void {
    this.generation++; if (this.timer) clearInterval(this.timer); this.timer = undefined;
    if (this.raf !== undefined) this.surface.cancelAnimationFrame?.(this.raf); this.raf = undefined;
    this.observer?.disconnect(); this.observer = undefined; this.detachVisibility?.(); this.detachVisibility = undefined;
  }
  dispose(): void { this.stop(); }
}
